import { useState, useMemo } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Plus, Money, Eye, Trash, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { bookingHours, driverPayFor } from "../lib/chauffeurSplit";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const driverName = (d) => `${d.first_name || ""} ${d.last_name || ""}`.trim() || "Unnamed";

const jobHours = bookingHours;
const fmtHrs = (h) => `${(Math.round((Number(h) || 0) * 10) / 10).toLocaleString()} h`;

// Pay a driver earns for one completed booking: hours × hourly rate.
// Already-paid jobs use the amount actually paid out.
const jobPay = (b, d) => {
  if (b.driver_paid && Number(b.driver_pay_amount) > 0) return Number(b.driver_pay_amount);
  return driverPayFor(b, d);
};

const norm = (s) => (s || "").trim().toLowerCase();
// A booking belongs to this driver when linked by id, or — for older/imported
// trips without a linked driver — when the stored driver name matches.
const isDriversJob = (b, d, full, first) => {
  if (b.driver_id === d.id) return true;
  if (b.driver_id) return false;
  const bn = norm(b.driver_name);
  return !!bn && (bn === full || (!!first && bn === first));
};

export default function DriversPanel({ chauffeurs, bookings, onChange }) {
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", rate: "", tour_rate: "" });
  const [saving, setSaving] = useState(false);
  const [editDriver, setEditDriver] = useState(null); // full driver doc being edited
  const [editForm, setEditForm] = useState({ name: "", rate: "", tour_rate: "" });
  const [editSaving, setEditSaving] = useState(false);
  const [profile, setProfile] = useState(null); // driver being viewed
  const [selected, setSelected] = useState({}); // booking_id -> bool
  const [paying, setPaying] = useState(false);

  // Per-driver completed jobs + earnings + hours driven.
  const stats = useMemo(() => {
    const map = {};
    for (const d of chauffeurs) {
      const full = norm(driverName(d));
      const first = norm(d.first_name);
      const jobs = bookings
        .filter((b) => b.status === "completed" && isDriversJob(b, d, full, first))
        .map((b) => ({ ...b, _pay: jobPay(b, d), _hours: jobHours(b) }))
        .sort((a, b) => Date.parse(b.start_datetime || b.booking_date || 0) - Date.parse(a.start_datetime || a.booking_date || 0));
      const totalMade = jobs.reduce((s, j) => s + j._pay, 0);
      const totalHours = jobs.reduce((s, j) => s + j._hours, 0);
      const paid = jobs.filter((j) => j.driver_paid);
      const unpaid = jobs.filter((j) => !j.driver_paid);
      const owed = unpaid.reduce((s, j) => s + j._pay, 0);
      const hoursPaid = paid.reduce((s, j) => s + j._hours, 0);
      const hoursUnpaid = unpaid.reduce((s, j) => s + j._hours, 0);
      map[d.id] = { jobs, paid, unpaid, totalMade, owed, totalHours, hoursPaid, hoursUnpaid };
    }
    return map;
  }, [chauffeurs, bookings]);

  const addDriver = async () => {
    if (!addForm.name.trim()) { toast.error("Driver name is required"); return; }
    setSaving(true);
    try {
      await api.post("/chauffeurs", {
        first_name: addForm.name.trim(),
        rate: Number(addForm.rate) || 0,
        tour_rate: Number(addForm.tour_rate) || 0,
        status: "active",
      });
      toast.success("Driver added");
      setAddOpen(false); setAddForm({ name: "", rate: "", tour_rate: "" });
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const removeDriver = async (id) => {
    if (!window.confirm("Remove this driver?")) return;
    try { await api.delete(`/chauffeurs/${id}`); toast.success("Driver removed"); onChange?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (d) => {
    setEditDriver(d);
    setEditForm({ name: driverName(d) === "Unnamed" ? "" : driverName(d), rate: d.rate ?? "", tour_rate: d.tour_rate ?? "" });
  };

  const saveEdit = async () => {
    if (!editForm.name.trim()) { toast.error("Driver name is required"); return; }
    setEditSaving(true);
    const parts = editForm.name.trim().split(/\s+/);
    try {
      await api.put(`/chauffeurs/${editDriver.id}`, {
        ...editDriver,
        first_name: parts[0],
        last_name: parts.slice(1).join(" "),
        rate: Number(editForm.rate) || 0,
        tour_rate: Number(editForm.tour_rate) || 0,
      });
      toast.success("Driver updated");
      setEditDriver(null);
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setEditSaving(false); }
  };

  const openProfile = (d) => { setProfile(d); setSelected({}); };

  const payout = async (bookingIds) => {
    if (!profile || bookingIds.length === 0) { toast.error("No unpaid jobs selected"); return; }
    setPaying(true);
    try {
      const { data } = await api.post(`/drivers/${profile.id}/payout`, { booking_ids: bookingIds });
      toast.success(`Paid ${money(data.total_paid)} across ${data.paid_count} job(s)`);
      setProfile(null); setSelected({}); onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPaying(false); }
  };

  const profStats = profile ? stats[profile.id] : null;
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const selectedTotal = profStats ? profStats.unpaid.filter((j) => selected[j.id]).reduce((s, j) => s + j._pay, 0) : 0;

  return (
    <div className="space-y-4" data-testid="drivers-panel">
      <div className="flex items-center justify-between">
        <div className="label-mono">Drivers & payouts</div>
        <Button size="sm" onClick={() => setAddOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-driver-button"><Plus size={14} className="mr-1.5" />Add Driver</Button>
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left px-4 py-3 label-mono font-normal">Driver</th>
              <th className="text-right px-4 py-3 label-mono font-normal">Chauffeur rate</th>
              <th className="text-right px-4 py-3 label-mono font-normal">Tour rate</th>
              <th className="text-right px-4 py-3 label-mono font-normal">Hours driven</th>
              <th className="text-right px-4 py-3 label-mono font-normal">Total made</th>
              <th className="text-right px-4 py-3 label-mono font-normal">Owed</th>
              <th className="text-left px-4 py-3 label-mono font-normal">Last paid out</th>
              <th className="text-right px-4 py-3 label-mono font-normal w-64">Actions</th>
            </tr>
          </thead>
          <tbody>
            {chauffeurs.length === 0 ? <tr><td colSpan={8} className="p-10 text-center text-muted-foreground">No drivers yet. Click &quot;Add Driver&quot;.</td></tr>
              : chauffeurs.map((d) => {
                const s = stats[d.id] || { totalMade: 0, owed: 0, totalHours: 0, hoursUnpaid: 0 };
                return (
                  <tr key={d.id} className="border-b border-border/40 hover:bg-secondary/20" data-testid={`driver-row-${d.id}`}>
                    <td className="px-4 py-3 font-heading">{driverName(d)}</td>
                    <td className="px-4 py-3 text-right">{money(d.rate)}/h</td>
                    <td className="px-4 py-3 text-right">{money(d.tour_rate)}/h</td>
                    <td className="px-4 py-3 text-right" data-testid={`driver-hours-${d.id}`}>
                      <div>{fmtHrs(s.totalHours)}</div>
                      {s.hoursUnpaid > 0 && <div className="text-[10px] text-amber-400">{fmtHrs(s.hoursUnpaid)} unpaid</div>}
                    </td>
                    <td className="px-4 py-3 text-right" data-testid={`driver-made-${d.id}`}>{money(s.totalMade)}</td>
                    <td className={`px-4 py-3 text-right font-heading ${s.owed > 0 ? "text-amber-400" : "text-muted-foreground"}`} data-testid={`driver-owed-${d.id}`}>{money(s.owed)}</td>
                    <td className="px-4 py-3 text-xs">{fmtDate(d.last_paid_out)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" onClick={() => openProfile(d)} data-testid={`driver-view-${d.id}`}><Eye size={12} className="mr-1" />View</Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(d)} data-testid={`driver-edit-${d.id}`}><PencilSimple size={12} className="mr-1" />Edit</Button>
                      <Button size="sm" variant="ghost" onClick={() => removeDriver(d.id)} data-testid={`driver-delete-${d.id}`}><Trash size={14} className="text-destructive" /></Button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Add driver dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="add-driver-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Add Driver</DialogTitle>
            <DialogDescription>Set the hourly pay for chauffeur and tour jobs — a driver earns hours × rate per job.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="label-mono mb-1.5 block">Name *</Label><Input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} className="bg-input border-border" data-testid="driver-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-mono mb-1.5 block">Chauffeur rate ($/hour)</Label><Input type="number" value={addForm.rate} onChange={(e) => setAddForm({ ...addForm, rate: e.target.value })} className="bg-input border-border" data-testid="driver-rate-input" /></div>
              <div><Label className="label-mono mb-1.5 block">Tour rate ($/hour)</Label><Input type="number" value={addForm.tour_rate} onChange={(e) => setAddForm({ ...addForm, tour_rate: e.target.value })} className="bg-input border-border" data-testid="driver-tourrate-input" /></div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={addDriver} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="driver-save-button">{saving ? "Saving…" : "Add Driver"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit driver dialog */}
      <Dialog open={!!editDriver} onOpenChange={(o) => !o && setEditDriver(null)}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="edit-driver-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Edit Driver</DialogTitle>
            <DialogDescription>Update the driver&apos;s name and hourly pay rates (pay = hours × rate per job).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="label-mono mb-1.5 block">Name *</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="bg-input border-border" data-testid="edit-driver-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-mono mb-1.5 block">Chauffeur rate ($/hour)</Label><Input type="number" value={editForm.rate} onChange={(e) => setEditForm({ ...editForm, rate: e.target.value })} className="bg-input border-border" data-testid="edit-driver-rate-input" /></div>
              <div><Label className="label-mono mb-1.5 block">Tour rate ($/hour)</Label><Input type="number" value={editForm.tour_rate} onChange={(e) => setEditForm({ ...editForm, tour_rate: e.target.value })} className="bg-input border-border" data-testid="edit-driver-tourrate-input" /></div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setEditDriver(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={editSaving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="edit-driver-save-button">{editSaving ? "Saving…" : "Save changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Driver profile / payout dialog */}      <Dialog open={!!profile} onOpenChange={(o) => !o && setProfile(null)}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="driver-profile-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{profile ? driverName(profile) : ""}</DialogTitle>
            <DialogDescription>Completed jobs and payouts.</DialogDescription>
          </DialogHeader>
          {profStats && (
            <>
              <div className="grid grid-cols-4 gap-3 my-2">
                <div className="border border-border p-3"><div className="label-mono text-xs">Hours driven</div><div className="font-heading text-xl" data-testid="profile-hours">{fmtHrs(profStats.totalHours)}</div>{profStats.hoursUnpaid > 0 && <div className="text-[10px] text-amber-400 mt-0.5">{fmtHrs(profStats.hoursUnpaid)} unpaid</div>}</div>
                <div className="border border-border p-3"><div className="label-mono text-xs">Total made</div><div className="font-heading text-xl">{money(profStats.totalMade)}</div></div>
                <div className="border border-border p-3"><div className="label-mono text-xs">Owed</div><div className="font-heading text-xl text-amber-400" data-testid="profile-owed">{money(profStats.owed)}</div></div>
                <div className="border border-border p-3"><div className="label-mono text-xs">Last paid</div><div className="text-sm mt-1">{fmtDate(profile.last_paid_out)}</div></div>
              </div>

              {/* Unpaid jobs with selection */}
              <div className="label-mono text-amber-400 mt-2 mb-1">Unpaid ({profStats.unpaid.length})</div>
              {profStats.unpaid.length === 0 ? <div className="text-sm text-muted-foreground px-1 py-2">Nothing outstanding.</div> : (
                <div className="border border-border divide-y divide-border/40">
                  {profStats.unpaid.map((j) => (
                    <label key={j.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-secondary/20" data-testid={`unpaid-job-${j.id}`}>
                      <input type="checkbox" checked={!!selected[j.id]} onChange={(e) => setSelected({ ...selected, [j.id]: e.target.checked })} data-testid={`unpaid-check-${j.id}`} />
                      <span className="flex-1">{j.customer_name || "Guest"} · <span className="capitalize text-muted-foreground">{j.booking_type}</span> · {fmtDate(j.start_datetime || j.booking_date)}</span>
                      <span className="text-[11px] text-muted-foreground w-14 text-right">{fmtHrs(j._hours)}</span>
                      <span className="font-heading">{money(j._pay)}</span>
                    </label>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => payout(profStats.unpaid.map((j) => j.id))} disabled={paying || profStats.unpaid.length === 0} data-testid="pay-all-button"><Money size={14} className="mr-1.5" />Pay all up to date ({money(profStats.owed)})</Button>
                <Button size="sm" onClick={() => payout(selectedIds)} disabled={paying || selectedIds.length === 0} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="pay-selected-button">Pay selected ({money(selectedTotal)})</Button>
              </div>

              {/* Paid jobs */}
              <div className="label-mono mt-5 mb-1">Paid ({profStats.paid.length})</div>
              {profStats.paid.length === 0 ? <div className="text-sm text-muted-foreground px-1 py-2">No payouts yet.</div> : (
                <div className="border border-border divide-y divide-border/40 max-h-48 overflow-y-auto">
                  {profStats.paid.map((j) => (
                    <div key={j.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`paid-job-${j.id}`}>
                      <span className="flex-1">{j.customer_name || "Guest"} · <span className="capitalize text-muted-foreground">{j.booking_type}</span> · {fmtDate(j.start_datetime || j.booking_date)}</span>
                      <span className="text-[11px] text-muted-foreground w-14 text-right">{fmtHrs(j._hours)}</span>
                      <span className="text-xs text-muted-foreground">paid {fmtDate(j.driver_paid_at)}</span>
                      <span className="font-heading">{money(j.driver_pay_amount || j._pay)}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setProfile(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
